// @ts-nocheck
import styled from 'styled-components/macro'
import React, { useState, useEffect } from 'react'
import ContactUsForm from '../components/contact-us/ContactUsForm'
import ContactUsCheckboxes from '../components/contact-us/ContactUsCheckboxes'
import { StyledGridContainer, StyledDivider } from '../components/contact-us/contact-us-styles'
import { useForm } from 'react-hook-form'
import { getECommerceDomainByEnv, sendRequest, getLocaleStr, createUrl } from 'utils/utils'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import isEmail from 'validator/lib/isEmail'
import ContactUsConfirmation from '../components/contact-us/ContactUsConfirmation'
import DialogBox from 'components/common/DialogBox'
import Backdrop from '@material-ui/core/Backdrop'
import LoadingSpinner from 'components/common/LoadingSpinner'
import Skeleton from 'components/Skeleton'
import SubmitButton from 'components/common/ButtonBase'
import { Page } from 'utils/constants'

// demos styles
const CommonStyles = styled.div`
	@media (min-width: 577px) {
		background: rgba(0, 0, 0, 0.7);
		overflow-x: hidden;
	}
`

const FormContainer = styled.div`
	@media (min-width: 577px) {
		width: 870px;
		margin: 27px auto;
		box-shadow: 0 2px 9px rgba(0, 0, 0, 0.5);
		padding: 40px 117px;
	}
	background-color: white;
`

const StyledBackdrop = styled(Backdrop)`
	z-index: 9999999;
	background-color: rgba(0, 0, 0, 0.6);
`

const PH_GERMANY_MIN_CHARS = 1
const PH_GERMANY_NAME_MAX_CHARS = 32
const PH_GERMANY_MESSAGE_MAX_CHARS = 10000

const nameRegex = /^[-\p{L} ]+$/u

const SubmitButtonContainer = styled.div`
	padding: 12px;
`

export const buildContactUsZodValidationForm = (lang: any) => {
	const schema = {
		firstName: z
			.string({
				required_error: lang.webviewFlow.firstNameError,
			})
			.trim()
			.min(1, { message: lang.webviewFlow.firstNameError })
			.min(PH_GERMANY_MIN_CHARS)
			.max(PH_GERMANY_NAME_MAX_CHARS)
			.regex(nameRegex, { message: lang.webviewFlow.firstNameError }),
		lastName: z
			.string({
				required_error: lang.webviewFlow.lastNameError,
			})
			.trim()
			.min(1, { message: lang.webviewFlow.lastNameError })
			.min(PH_GERMANY_MIN_CHARS)
			.max(PH_GERMANY_NAME_MAX_CHARS)
			.regex(nameRegex, { message: lang.webviewFlow.lastNameError }),
		yourMessage: z
			.string({ required_error: lang.webviewFlow.yourMessageError })
			.trim()
			.min(1, { message: lang.webviewFlow.yourMessageError })
			.min(PH_GERMANY_MIN_CHARS)
			.max(PH_GERMANY_MESSAGE_MAX_CHARS),
		email: z
			.string({ required_error: lang.webviewFlow.pleaseShareEmailAddress })
			.trim()
			.min(1, { message: lang.webviewFlow.pleaseShareEmailAddress })
			.regex(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, { message: lang.webviewFlow.pleaseShareEmailAddress })
			.refine((val) => isEmail(val, { require_tld: true }), { message: lang.webviewFlow.pleaseShareEmailAddress }),
		phone: z
			.string({ required_error: lang.webviewFlow.pleaseSharePhoneNumber })
			.trim()
			.min(1, { message: lang.webviewFlow.pleaseSharePhoneNumber })
			.min(8)
			.max(20)
			.regex(/^[+]?[0-9 \-().]{8,20}$/, { message: lang?.webviewFlow?.invalidPhoneNumber || `invalid phone number (ex. 1234567890)` }),
		orderType: z
			.string({ required_error: lang.webviewFlow.pleaseChooseWayOfOrderError })
			.trim()
			.min(1, { message: lang.webviewFlow.pleaseChooseWayOfOrderError }),
		store: z.string().nullish(),
		topic: z
			.string({ required_error: lang.webviewFlow.pleaseSelectTopicError })
			.trim()
			.min(1, { message: lang.webviewFlow.pleaseSelectTopicError }),
		areaOfMessage: z
			.string({ required_error: lang.webviewFlow.pleaseChooseAreaOfMessageError })
			.trim()
			.min(1, { message: lang.webviewFlow.pleaseChooseAreaOfMessageError }),
		uploadFile: z.array(z.any()).optional(),
	}
	return schema
}

const ContactUs = (props: any) => {
	const { chainId, chainStores, lang, eCommerce, conversationText, params } = props
	const contactUsLang = conversationText

	const formValidationSchema = buildContactUsZodValidationForm(contactUsLang)
	const schema = z.object(formValidationSchema)

	const [fieldsToValidate, setFieldsToValidate] = useState(Object.keys(formValidationSchema))
	const [isCheckboxChosen, setIsCheckboxChosen] = useState(false)
	const [showCheckboxError, setShowCheckboxError] = useState(false)
	const [showConfirmationPage, setShowConfirmationPage] = useState(false)
	const [shouldBeContacted, setShouldBeContacted] = useState(true)
	const [showDialogBox, setShowDialogBox] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [emailRecipientsId, setEmailRecipientsId] = useState(chainId)
	const region = params.country || ''

	const {
		handleSubmit,
		formState: { errors, isValid },
		getValues,
		setError,
		clearErrors,
		control,
		trigger,
		setValue,
		register,
		unregister,
	} = useForm({
		resolver: zodResolver(schema),
		mode: 'onChange',
	})

	const currentStore = chainStores.msg.find?.((store: any) => getLocaleStr(store.title, lang) === getValues('store'))

	useEffect(() => {
		if (isCheckboxChosen) {
			setShowCheckboxError(false)
		}
	}, [isCheckboxChosen])

	// define the chain/store id of the chosen store in contact us form, or chain id by default
	// in order to send the form to the correct recipients
	useEffect(() => {
		if (currentStore) {
			setEmailRecipientsId(currentStore.id)
		} else {
			setEmailRecipientsId(chainId)
		}
	}, [chainId, currentStore])

	const listenToCheckboxes = (checkboxesState: any) => {
		let fields = Object.keys(formValidationSchema)
		const checkboxesValues = Object.values(checkboxesState).filter((checkboxValue) => checkboxValue === true).length
		if (!checkboxesState.email) {
			fields = fields.filter((fieldName) => fieldName !== 'email')
		}
		if (!checkboxesState.phone) {
			fields = fields.filter((fieldName) => fieldName !== 'phone')
		}
		setFieldsToValidate([...fields])
		setIsCheckboxChosen(checkboxesValues >= 1)
	}

	const onSubmit = async () => {
		await trigger(fieldsToValidate)

		if (!Object.keys(errors).length && isCheckboxChosen) {
			const sentData = { storeId: emailRecipientsId, form: getValues(), region, lang, chainId }
			try {
				setIsLoading(true)
				const contactUsSendRes = await sendRequest(true, createUrl(getECommerceDomainByEnv(params), `/v1/contact-us`), 'post', sentData)
				if (contactUsSendRes.status === 'success') {
					setShowConfirmationPage(true)
					setIsLoading(false)
				}
			} catch (e) {
				console.error(e)
				setShowDialogBox(true)
				setIsLoading(false)
			}
		} else if (!isCheckboxChosen) {
			// no checkbox chosen leads to showing an error and not sending the form
			console.error('no checkbox chosen!')
			setShowCheckboxError(true)
		}
	}

	const handleShouldBeContacted = (status: any) => {
		setShouldBeContacted(status)
	}

	return (
		<>
			<CommonStyles>
				<Skeleton page={Page.CONTACT_US} showLogo showCustomTitle showHeader>
					{showConfirmationPage ? (
						<ContactUsConfirmation
							eCommerce={eCommerce}
							lang={contactUsLang}
							confirmationText={
								shouldBeContacted
									? contactUsLang.webviewFlow.thankYouForYourMessageContact
									: contactUsLang.webviewFlow.thankYouForYourMessageNoContact
							}
							confirmationBtnText={contactUsLang.webviewFlow.backToHomepage}
						/>
					) : (
						<FormContainer>
							<form id="hook-form-details" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
								<StyledGridContainer container direction="row">
									<ContactUsForm
										control={control}
										setError={setError}
										register={register}
										getValues={getValues}
										clearErrors={clearErrors}
										setValue={setValue}
										chainId={chainId}
										branchesList={chainStores}
										lang={contactUsLang}
										languageForLocale={lang}
									/>
								</StyledGridContainer>

								<StyledDivider />

								<StyledGridContainer container direction="row" $isCheckboxContainer>
									<ContactUsCheckboxes
										control={control}
										listenToCheckboxes={listenToCheckboxes}
										showError={showCheckboxError}
										clearErrors={clearErrors}
										setValue={setValue}
										unregister={unregister}
										lang={contactUsLang}
										shouldBeContacted={handleShouldBeContacted}
									/>
								</StyledGridContainer>

								<SubmitButtonContainer>
									<SubmitButton $size="big" onClick={onSubmit} data-testid="submit-button">
										{contactUsLang.webviewFlow.send}
									</SubmitButton>
								</SubmitButtonContainer>
							</form>
						</FormContainer>
					)}
					{showDialogBox && (
						<DialogBox
							title={contactUsLang.webviewFlow.serverError}
							message={contactUsLang.webviewFlow.serverErrorMessage}
							okText={contactUsLang.webviewFlow.ok}
							open={showDialogBox}
							okAction={() => {
								setShowDialogBox(false)
							}}
						/>
					)}
				</Skeleton>
			</CommonStyles>
			{isLoading && (
				<StyledBackdrop open={isLoading}>
					<LoadingSpinner brand={params.brand} />
				</StyledBackdrop>
			)}
		</>
	)
}

export default ContactUs
